import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

// Create axios instance
const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json'
  }
});

// Add token to requests
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Handle response errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

const multipartConfig = {
  transformRequest: [
    (data, headers) => {
      if (typeof FormData !== 'undefined' && data instanceof FormData) {
        delete headers['Content-Type'];
      }
      return data;
    }
  ]
};

// Auth API
export const authAPI = {
  register: (data) => api.post('/auth/register', data),
  login: (data) => api.post('/auth/login', data),
  forgotPassword: (data) => api.post('/auth/forgot-password', data),
  resetPassword: (data) => api.post('/auth/reset-password', data),
  getCurrentUser: () => api.get('/auth/me'),
  updateProfile: (data) => api.patch('/auth/me', data),
  changePassword: (data) => api.post('/auth/me/password', data),
  uploadProfilePhoto: (formData) => api.post('/auth/me/profile-photo', formData, multipartConfig)
};

// Admin API
export const adminAPI = {
  getAllUsers: () => api.get('/admin/users'),
  updateUserStatus: (userId, data) => api.patch(`/admin/users/${userId}/status`, data),
  getAllWallets: () => api.get('/admin/wallets'),
  getWallet: (phlebotomistId) => api.get(`/admin/wallets/${phlebotomistId}`),
  approveTransaction: (transactionId, data) => api.patch(`/admin/wallets/transactions/${transactionId}/approve`, data),
  processPayout: (phlebotomistId, data) => api.post(`/admin/wallets/${phlebotomistId}/payout`, data),
  adjustWallet: (phlebotomistId, data) => api.post(`/admin/wallets/${phlebotomistId}/adjust`, data),
  getAllReports: (params) => api.get('/admin/reports', { params }),
  updateReportBilling: (reportId, data) => api.patch(`/admin/reports/${reportId}/billing`, data),
  getDashboardStats: () => api.get('/admin/dashboard/stats'),
  getAuditLogs: (params) => api.get('/admin/audit-logs', { params }),
  getAuditLogResourceTypes: () => api.get('/admin/audit-logs/resource-types'),
  getDemographicsBatches: () => api.get('/admin/demographics/batches'),
  exportDemographicsExcel: (params) =>
    api.get('/admin/demographics/export', { params, responseType: 'blob' }),
  getDemographicsUiOptions: () => api.get('/admin/demographics/ui-options'),
  patchDemographicsUiOptions: (data) => api.patch('/admin/demographics/ui-options', data)
};

// Lab API
export const labAPI = {
  getDashboard: () => api.get('/lab/dashboard'),
  getAnalytics: () => api.get('/lab/analytics'),
  addClinic: (data) => api.post('/lab/clinics', data),
  inviteClinic: (data) => api.post('/lab/clinics/invite', data),
  getClinics: () => api.get('/lab/clinics'),
  updateClinic: (clinicId, data) => api.patch(`/lab/clinics/${clinicId}`, data),
  disconnectClinic: (clinicId) => api.delete(`/lab/clinics/${clinicId}`),
  createPickupRequest: (data) => api.post('/lab/pickup-requests', data),
  getPickupRequests: () => api.get('/lab/pickup-requests'),
  updatePickupStatus: (id, data) => api.patch(`/lab/pickup-requests/${id}/status`, data),
  notifyClinicDelay: (id, data) => api.patch(`/lab/pickup-requests/${id}/delay`, data),
  createJob: (data) => api.post('/lab/jobs', data),
  getJobs: () => api.get('/lab/jobs'),
  acceptBid: (jobId, bidId) => api.patch(`/lab/jobs/${jobId}/bids/${bidId}/accept`),
  markJobPaid: (jobId) => api.patch(`/lab/jobs/${jobId}/mark-paid`),
  createReagentRequest: (data) => api.post('/lab/reagent-requests', data),
  getReagentRequests: () => api.get('/lab/reagent-requests'),
  acceptReagentOffer: (requestId, offerId) => api.patch(`/lab/reagent-requests/${requestId}/offers/${offerId}/accept`),
  uploadReport: (data) => api.post('/lab/reports', data),
  getReports: () => api.get('/lab/reports'),
  getAuditLogs: (params) => api.get('/lab/audit-logs', { params }),
  getServiceRequests: (params) => api.get('/lab/service-requests', { params }),
  updateServiceRequest: (id, data) => api.patch(`/lab/service-requests/${id}`, data),
  uploadLabLogo: (formData) => api.post('/lab/logo', formData, multipartConfig),
  uploadServiceRequestFiles: (id, formData) =>
    api.post(`/lab/service-requests/${id}/attachments`, formData, multipartConfig),
  postDemographicsPdf: (formData) =>
    api.post('/lab/demographics/batches', formData, multipartConfig),
  getDemographicsBatches: () => api.get('/lab/demographics/batches'),
  deleteDemographicsBatch: (batchId) => api.delete(`/lab/demographics/batches/${batchId}`),
  getDemographicsTrends: () => api.get('/lab/demographics/trends'),
  getDemographicsStats: () => api.get('/lab/demographics/stats'),
  getDemographicsAnalytics: (params) => api.get('/lab/demographics/analytics', { params }),
  getDemographicsPanelOptions: () => api.get('/lab/demographics/analytics/panel-options'),
  getDemographicsUiOptions: () => api.get('/lab/demographics/ui-options'),
  getDemographicsRows: (params) => api.get('/lab/demographics/rows', { params }),
  patchDemographicsRow: (rowId, data) => api.patch(`/lab/demographics/rows/${rowId}`, data),
  exportDemographicsExcel: (params) =>
    api.get('/lab/demographics/export', { params, responseType: 'blob' }),
  downloadServiceAttachment: (requestId, attachmentId) =>
    api.get(`/lab/service-requests/${requestId}/attachments/${attachmentId}`, { responseType: 'blob' }),
  getLabTeam: () => api.get('/lab/team'),
  patchEmployeeStaffRole: (userId, data) => api.patch(`/lab/employees/${userId}/staff-role`, data),
  getLabTasks: () => api.get('/lab/tasks'),
  createLabTask: (data) => api.post('/lab/tasks', data),
  updateLabTask: (taskId, data) => api.patch(`/lab/tasks/${taskId}`, data),
  deleteLabTask: (taskId) => api.delete(`/lab/tasks/${taskId}`),
  reorderLabTasks: (data) => api.patch('/lab/tasks/reorder', data)
};

// Clinic API
export const clinicAPI = {
  getDashboard: () => api.get('/clinic/dashboard'),
  getAnalytics: () => api.get('/clinic/analytics'),
  getLabs: () => api.get('/clinic/labs'),
  submitPickupRequest: (data) => api.post('/clinic/pickup-requests', data),
  getPickupRequests: () => api.get('/clinic/pickup-requests'),
  getReports: () => api.get('/clinic/reports'),
  getReport: (reportId) => api.get(`/clinic/reports/${reportId}`),
  createServiceRequest: (data) => api.post('/clinic/service-requests', data),
  getServiceRequests: () => api.get('/clinic/service-requests'),
  patchServiceRequest: (id, data) => api.patch(`/clinic/service-requests/${id}`, data),
  getAuditLogs: (params) => api.get('/clinic/audit-logs', { params }),
  getDemographicsStats: () => api.get('/clinic/demographics/stats'),
  getDemographicsAnalytics: (params) => api.get('/clinic/demographics/analytics', { params }),
  getDemographicsPanelOptions: () => api.get('/clinic/demographics/analytics/panel-options'),
  getDemographicsRows: (params) => api.get('/clinic/demographics/rows', { params }),
  downloadServiceAttachment: (requestId, attachmentId) =>
    api.get(`/clinic/service-requests/${requestId}/attachments/${attachmentId}`, { responseType: 'blob' })
};

// Phlebotomist API
export const phlebotomistAPI = {
  getDashboard: () => api.get('/phlebotomist/dashboard'),
  getAvailableJobs: () => api.get('/phlebotomist/jobs/available'),
  submitBid: (jobId, data) => api.post(`/phlebotomist/jobs/${jobId}/bid`, data),
  getMyJobs: () => api.get('/phlebotomist/jobs/my-jobs'),
  acceptJob: (jobId) => api.patch(`/phlebotomist/jobs/${jobId}/accept`),
  declineJob: (jobId, data) => api.patch(`/phlebotomist/jobs/${jobId}/decline`, data),
  updateJobStatus: (jobId, data) => api.patch(`/phlebotomist/jobs/${jobId}/status`, data),
  getWallet: () => api.get('/phlebotomist/wallet'),
  updatePayoutLink: (data) => api.patch('/phlebotomist/payout-link', data)
};

// Supplier API
export const supplierAPI = {
  getDashboard: () => api.get('/supplier/dashboard'),
  getReagentRequests: () => api.get('/supplier/reagent-requests'),
  submitOffer: (requestId, data) => api.post(`/supplier/reagent-requests/${requestId}/offer`, data),
  getMyOffers: () => api.get('/supplier/offers/my-offers')
};

// Jobs API
export const jobsAPI = {
  getPublicJobs: () => api.get('/jobs/public')
};

// Subscriptions API
export const subscriptionsAPI = {
  getPlans: (params) => api.get('/subscription-plans', { params }),
  getSubscription: () => api.get('/subscriptions'),
  upgradeSubscription: (data) => api.post('/subscriptions/upgrade', data),
  createCheckoutSession: (data) => api.post('/subscriptions/create-checkout-session', data)
};

// Subscription Plans API (Admin CRUD)
export const subscriptionPlansAPI = {
  getPlans: (params) => api.get('/subscription-plans', { params }),
  createPlan: (data) => api.post('/subscription-plans', data),
  updatePlan: (planId, data) => api.patch(`/subscription-plans/${planId}`, data),
  deletePlan: (planId) => api.delete(`/subscription-plans/${planId}`),
  assignPlanToLab: (labId, data) => api.post(`/subscription-plans/assign/${labId}`, data)
};

// Messaging / conversations
export const messagingAPI = {
  listConversations: () => api.get('/conversations'),
  createOrGet: (data) => api.post('/conversations', data),
  getMessages: (conversationId) => api.get(`/conversations/${conversationId}/messages`),
  sendMessage: (conversationId, data) => api.post(`/conversations/${conversationId}/messages`, data),
  markRead: (conversationId) => api.patch(`/conversations/${conversationId}/read`)
};

// Bills API
export const billsAPI = {
  getBills: (params) => api.get('/bills', { params }),
  getBill: (billId) => api.get(`/bills/${billId}`),
  createBill: (data) => api.post('/bills', data),
  createBillForJob: (jobId, data) => api.post(`/bills/job/${jobId}`, data),
  createBillForSupply: (requestId, data) => api.post(`/bills/supply/${requestId}`, data),
  createCheckoutSession: (billId, data) => api.post(`/bills/${billId}/create-checkout-session`, data),
  markBillPaid: (billId, data) => api.patch(`/bills/${billId}/mark-paid`, data)
};

export default api;

